import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  CircularProgress,
  Alert,
} from '@mui/material';
import { PieChart, Pie, Cell, Legend, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { issueApi } from '../api/issueApi';

interface StatisticsData {
  total: number;
  by_status: Record<string, { count: number; percentage: number }>;
  by_rank: Record<string, { count: number; percentage: number }>;
}

interface ChartDataPoint {
  name: string;
  value: number;
  percentage: number;
  statusKey?: string;
}

interface IssuesStatisticsChartProps {
  projectId: number;
  chartType?: 'status' | 'rank' | 'both';
}

const STATUS_COLORS: Record<string, string> = {
  'draft': '#D3D3D3',
  'waiting_response': '#FF9800',
  'inprogress': '#FFC107',
  'waiting_approval': '#8BC34A',
  'completed': '#4CAF50',
};

const RANK_COLORS: Record<string, string> = {
  'a': '#F44336',
  'b': '#FFC107',
  'c': '#4CAF50',
};

const formatStatusLabel = (status: string): string => {
  return status
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const formatRankLabel = (rank: string): string => {
  return `Rank ${rank.toUpperCase()}`;
};

export const IssuesStatisticsChart: React.FC<IssuesStatisticsChartProps> = ({ projectId, chartType = 'both' }) => {
  const [statistics, setStatistics] = useState<StatisticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStatistics = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await issueApi.getStatistics(projectId);
        setStatistics(data);
      } catch (err) {
        console.error('Failed to fetch statistics:', err);
        setError('Failed to load statistics');
      } finally {
        setLoading(false);
      }
    };

    if (projectId) {
      fetchStatistics();
    }
  }, [projectId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!statistics || statistics.total === 0) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No issues found for this project
      </Alert>
    );
  }

  // Prepare data for status chart in fixed order
  const statusOrder = ['draft', 'waiting_response', 'inprogress', 'waiting_approval', 'completed'];
  const statusChartData: ChartDataPoint[] = statusOrder
    .filter(status => statistics.by_status[status])
    .map(status => {
      const data = statistics.by_status[status];
      return {
        name: formatStatusLabel(status),
        value: data.count,
        percentage: data.percentage,
        statusKey: status,
      };
    });

  // Prepare data for rank chart
  const rankChartData: ChartDataPoint[] = Object.entries(statistics.by_rank).map(
    ([rank, data]) => ({
      name: formatRankLabel(rank),
      value: data.count,
      percentage: data.percentage,
    })
  );

  const renderCustomLabel = (entry: any) => {
    return `${entry.percentage}%`;
  };

  // Render only status chart
  if (chartType === 'status') {
    return (
      <Box>
        <ResponsiveContainer width="100%" height={160}>
          <PieChart margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
            <Pie
              data={statusChartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={renderCustomLabel}
              outerRadius={50}
              fill="#8884d8"
              dataKey="value"
            >
              {statusChartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={STATUS_COLORS[(entry as any).statusKey] || '#8884d8'}
                />
              ))}
            </Pie>
            <Tooltip
              formatter={(value: number, name: string, props: any) => [
                `${value} issues (${props.payload.percentage}%)`,
                'Count',
              ]}
            />
          </PieChart>
        </ResponsiveContainer>
        <Box sx={{ mt: 2 }}>
          {statusChartData.map((item, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 0.5,
                borderBottom: '1px solid #eee',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor:
                      STATUS_COLORS[(item as any).statusKey] || '#8884d8',
                  }}
                />
                <Typography variant="body2">{item.name}</Typography>
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {item.value}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  // Render only rank chart
  if (chartType === 'rank') {
    return (
      <Box>
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={rankChartData} margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip
              formatter={(value: number, name: string, props: any) => [
                `${value} issues (${props.payload.percentage}%)`,
                'Count',
              ]}
            />
            <Bar dataKey="value" fill="#8884d8" radius={[8, 8, 0, 0]}>
              {rankChartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={RANK_COLORS[Object.keys(statistics.by_rank)[index]] || '#8884d8'}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <Box sx={{ mt: 2 }}>
          {rankChartData.map((item, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                py: 0.5,
                borderBottom: '1px solid #eee',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '2px',
                    backgroundColor:
                      RANK_COLORS[Object.keys(statistics.by_rank)[index]] || '#8884d8',
                  }}
                />
                <Typography variant="body2">{item.name}</Typography>
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {item.value}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  // Render both charts (default)
  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
        BIRA Statistics
      </Typography>

      <Grid container spacing={3}>
        {/* Status Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ textAlign: 'center', mb: 2 }}>
              BIRA by Status
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={renderCustomLabel}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {statusChartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={STATUS_COLORS[Object.keys(statistics.by_status)[index]] || '#8884d8'}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number, name: string, props: any) => [
                    `${value} issues (${props.payload.percentage}%)`,
                    'Count',
                  ]}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
            <Box sx={{ mt: 2 }}>
              {statusChartData.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 0.5,
                    borderBottom: '1px solid #eee',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor:
                          STATUS_COLORS[Object.keys(statistics.by_status)[index]] || '#8884d8',
                      }}
                    />
                    <Typography variant="body2">{item.name}</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {item.value} ({item.percentage}%)
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Rank Chart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ textAlign: 'center', mb: 2 }}>
              BIRA by Rank
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={rankChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip
                  formatter={(value: number, name: string, props: any) => [
                    `${value} issues (${props.payload.percentage}%)`,
                    'Count',
                  ]}
                />
                <Bar dataKey="value" fill="#8884d8" radius={[8, 8, 0, 0]}>
                  {rankChartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={RANK_COLORS[Object.keys(statistics.by_rank)[index]] || '#8884d8'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <Box sx={{ mt: 2 }}>
              {rankChartData.map((item, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 0.5,
                    borderBottom: '1px solid #eee',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '2px',
                        backgroundColor:
                          RANK_COLORS[Object.keys(statistics.by_rank)[index]] || '#8884d8',
                      }}
                    />
                    <Typography variant="body2">{item.name}</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {item.value} ({item.percentage}%)
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Summary Stats */}
      <Paper sx={{ p: 3, mt: 3, backgroundColor: '#f5f5f5' }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2196F3' }}>
                {statistics.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Issues
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4CAF50' }}>
                {statistics.by_status['completed']?.count || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Completed
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#FF9800' }}>
                {statistics.by_status['waiting_response']?.count || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Waiting Response
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#F44336' }}>
                {statistics.by_rank['a']?.count || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Rank A Issues
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};
